import axios from "axios"
import Swal from "sweetalert2";
import { AuthService } from "./auth.service";
import { LoginResponse, CodeRequestResponse, ResetVerifyResponse } from "../models/login.model";
import { ListModel } from "../models/list.model";
import { UserModel } from "../models/user.model";
import { AuthModel } from "../models/auth.model";
import { ActivityModel } from "../models/activity.model";
import { getCurrentDate } from "../lib/utils";
import { SensorModel } from "../models/sensor.model";
import { WalkModel, WalkPointModel } from "../models/walk.model";
import { LocateRequestResponse, LocateStatusResponse } from "../models/locate.model";
import { DeviceHealth } from "../models/deviceHealth.model";
import { BloodPressureReading, BloodPressureResponse } from "../models/bloodPressure.model";
import { WatchHrSample, WatchDay, WatchActivityState } from "../models/watchHealth.model";
import { SleepSoundNight, SleepSoundNightDetail, RadarSleepNight } from "../models/sleepSound.model";
import { v4 as uuidv4 } from 'uuid';

// Modern wellnuo-backend (app.wellnuo.com) — used for /api/me/* endpoints
// that don't live on the legacy Python well-api dispatcher. The auto-login
// flow already does direct CORS calls against this origin, so no proxy is
// needed; if CORS is ever restricted to mobile.wellnuo.com only, swap to
// a Traefik proxy and update WELLNUO_API_BASE to '' (same-origin).
const WELLNUO_API_BASE = 'https://app.wellnuo.com'

const wellnuoClient = axios.create({
    baseURL: WELLNUO_API_BASE,
    headers: { 'Content-Type': 'application/json' },
    // Send the HttpOnly `accessToken` cookie (modern JWT, Domain=.wellnuo.com,
    // SameSite=Lax) set by /login·verify-otp·exchange-jwt-for-cookie. mobile and
    // app are the same site (wellnuo.com), so the Lax cookie rides these XHRs and
    // authenticates /api/me/* even when localStorage holds only a legacy token.
    // The backend CORS for app.wellnuo.com sends ACAC:true + specific origin, so
    // credentialed requests are allowed.
    withCredentials: true,
    validateStatus() { return true }
})

export const customSwalStyling = {
    popup: "rounded-lg bg-white shadow-lg p-6 dark:bg-gray-800",
    title: "text-lg font-semibold text-gray-900 dark:text-gray-100",
    htmlContainer: "text-gray-600 dark:text-gray-300",
    confirmButton: "bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded",
    cancelButton: "bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded",
}

// Form-encoded client for the legacy /function/well-api/api dispatcher
// (dashboard_list, activities_report_details, sensor data, etc.).
const client = axios.create({
    baseURL: process.env.REACT_APP_API_BASE,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'mode': 'cors'
    },
    validateStatus() {
        return true
    }
})

// JSON client for /api/auth/* and other modern REST endpoints.
const jsonClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE,
    headers: {
        'Content-Type': 'application/json',
        'mode': 'cors'
    },
    validateStatus() {
        return true
    }
})

// Persist auth from a /api/auth/login or /api/auth/verify-otp success body.
function saveAuthFromResponse(rsp: LoginResponse) {
    if (!rsp.token || !rsp.user) return
    // Legacy /function/well-api/api controllers expect a non-empty user_name.
    // For email-only new users we fall back to the email so requests still
    // carry a recognisable identifier (controllers don't filter by it today).
    const username = rsp.user.username || rsp.user.email
    AuthService.saveAuth({
        username,
        email: rsp.user.email,
        token: rsp.token,
        user_id: rsp.user.id,
        profileComplete: rsp.profileComplete,
        needsProfile: rsp.isNewUser === true || rsp.profileComplete === false,
    })
}

export class DataService {
    /**
     * Email + password sign-in. The "email" field accepts a legacy user_name
     * for users who have one (the backend branches by '@' shape — see PLAN.md
     * §9.1 / gap G4). When `silent` is true, the built-in error toast is
     * suppressed so the caller can offer a pivot to the OTP path instead.
     */
    static async login(emailOrUsername: string, password: string, opts: { silent?: boolean } = {}): Promise<boolean> {
        if (emailOrUsername === '' && password === '') return false
        const silent = opts.silent === true

        try {
            const rsp = await jsonClient.request<LoginResponse>({
                url: '/api/auth/login',
                method: 'POST',
                data: { email: emailOrUsername, password }
            })

            if (rsp.status === 200 && rsp.data.success && rsp.data.token) {
                saveAuthFromResponse(rsp.data)
                return true
            }

            if (rsp.status === 200 && rsp.data.requires2FA) {
                // 2FA branch is not yet implemented in the PWA. Surface
                // a clear message rather than silently failing.
                Swal.fire({
                    icon: "info",
                    title: "Two-factor authentication required",
                    text: "This account uses 2FA. Please sign in via the web at app.wellnuo.com.",
                    customClass: customSwalStyling
                })
                return false
            }

            if (!silent) {
                Swal.fire({
                    icon: "error",
                    title: "Login Failed",
                    text: rsp.data.error || "Invalid email or password.",
                    customClass: customSwalStyling
                })
            }
        } catch (e) {
            if (!silent) {
                Swal.fire({
                    icon: "error",
                    title: "Server Unreachable",
                    text: "Something went wrong while contacting our servers, please try again later!",
                    customClass: customSwalStyling
                })
            }
        }

        return false
    }

    /**
     * Send a 6-digit sign-in code to the email.
     * Optional inviteCode is logged on successful verify (no validation).
     * Always resolves true on a 200 OK — the response is intentionally generic
     * to avoid email enumeration.
     */
    static async requestSigninCode(email: string, inviteCode: string = ''): Promise<boolean> {
        const rsp = await jsonClient.request<CodeRequestResponse>({
            url: '/api/auth/request-otp',
            method: 'POST',
            data: { email, ...(inviteCode ? { inviteCode } : {}) }
        })
        if (rsp.status === 200 && rsp.data.success) return true
        throw new Error(rsp.data.error || rsp.data.message || 'Failed to send code')
    }

    /**
     * Verify a 6-digit OTP. On success returns { isNewUser } and stores
     * auth in localStorage; throws on invalid/expired/locked-out code.
     */
    static async verifySigninCode(email: string, code: string): Promise<{ isNewUser: boolean }> {
        const rsp = await jsonClient.request<LoginResponse>({
            url: '/api/auth/verify-otp',
            method: 'POST',
            data: { email, code }
        })
        if (rsp.status === 200 && rsp.data.success && rsp.data.token) {
            saveAuthFromResponse(rsp.data)
            return { isNewUser: rsp.data.isNewUser === true }
        }
        throw new Error(rsp.data.error || 'Invalid or expired code')
    }

    /**
     * Request a 6-digit password-reset code. Always resolves true on a 200 OK
     * (generic response to avoid enumeration).
     */
    static async requestPasswordReset(email: string): Promise<boolean> {
        const rsp = await jsonClient.request<CodeRequestResponse>({
            url: '/api/auth/forgot-password',
            method: 'POST',
            data: { email }
        })
        if (rsp.status === 200 && rsp.data.success) return true
        throw new Error(rsp.data.error || rsp.data.message || 'Failed to send reset code')
    }

    /**
     * Submit reset code + new password. Throws on weak password, wrong code, etc.
     */
    static async verifyResetCode(email: string, code: string, newPassword: string): Promise<boolean> {
        const rsp = await jsonClient.request<ResetVerifyResponse>({
            url: '/api/auth/verify-reset-code',
            method: 'POST',
            data: { email, code, newPassword }
        })
        if (rsp.status === 200 && rsp.data.success) return true
        throw new Error(rsp.data.error || rsp.data.message || 'Password reset failed')
    }

    /**
     * Save first/last name on the current user. Used by the post-auth
     * name-capture modal when isNewUser/profileComplete=false.
     */
    static async updateProfile(firstName: string, lastName: string): Promise<boolean> {
        const auth: AuthModel = AuthService.getAuth()
        const rsp = await jsonClient.request<{ success?: boolean; error?: string }>({
            url: '/api/auth/profile',
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json'
            },
            data: { firstName, lastName }
        })
        if (rsp.status === 200 && rsp.data.success) {
            AuthService.updateAuth({ profileComplete: true, needsProfile: false })
            return true
        }
        throw new Error(rsp.data.error || 'Failed to save name')
    }

    static async getUsers(silent: boolean, forDate: string = getCurrentDate()): Promise<ListModel<UserModel>> {
        if (!silent) {
            Swal.fire({
                title: "Please wait",
                text: "Beneficiaries are getting processed!",
                customClass: customSwalStyling,
                didOpen: () => {
                    Swal.showLoading()
                }
            })
        }

        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<ListModel<UserModel>>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "dashboard_list",
                "user_name": auth.username,
                "token": auth.token,
                "date": forDate,
                "nonce": uuidv4()
            }
        })


        if (rsp.data.status !== "200 OK" || rsp.status !== 200) {
            this.throwSessionExpired()
        }

        Swal.close()
        return rsp.data
    }

    static async getUserById(id: number, silent: boolean, forDate: string = getCurrentDate()): Promise<UserModel> {
        if (!silent) {
            Swal.fire({
                title: "Please wait",
                text: "Latest details are getting loaded!",
                customClass: customSwalStyling,
                didOpen: () => {
                    Swal.showLoading()
                }
            })
        }

        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<ListModel<UserModel>>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "dashboard_single",
                "user_name": auth.username,
                "token": auth.token,
                "deployment_id": id,
                "date": forDate,
                "nonce": uuidv4()
            }
        })

        if (rsp.data.status !== "200 OK" || rsp.status !== 200) {
            this.throwSessionExpired()
        }

        Swal.close()
        return rsp.data.result_list[0]
    }

    static async getAcitivityReport(id: number): Promise<ActivityModel> {
        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<ActivityModel>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "activities_report_details",
                "user_name": auth.username,
                "token": auth.token,
                "deployment_id": id,
                "nonce": uuidv4()
            }
        })
        // Server may return 500 (e.g. deployment with empty timezone) with a
        // body that lacks chart_data. Surface a friendly error rather than
        // letting the caller crash on `rsp.chart_data.length`.
        if (rsp.status !== 200 || (rsp.data as any).status !== "200 OK" || !Array.isArray(rsp.data?.chart_data))
            throw new Error("Activity report unavailable")
        return rsp.data
    }

    static async getSensorData(id: number, sensor: string, room: string, forDate: string = getCurrentDate(), wellId?: number) {
        //["temperature", "humidity", "pressure", "light", "radar", "voc", "co2,]
        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<SensorModel>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "get_sensor_bucketed_data_by_room_sensor",
                "user_name": auth.username,
                "token": auth.token,
                "deployment_id": id,
                "sensor": sensor,
                'data_type': 'ML',
                'radar_part': 's28',
                "date": forDate,
                "bucket_size": '5m', //(10s, 1m, 5m, 10m, 15m, 30m, 1h)
                // Prefer well_id (well-api v537+): unique per room, immune to
                // duplicate base names / label drift. `location` kept for the
                // server's legacy fallback — it prefers well_id when both present.
                location: room,
                ...(wellId != null ? { well_id: wellId } : {}),
                "nonce": uuidv4()
            }
        })

        if (rsp.data.status !== "200 OK" || rsp.status !== 200)
            throw new Error(`Failed to load details for ${sensor}`)
        return rsp.data
    }

    // Walks live on wellnuo-backend, not on the legacy well-api. We need a
    // modern Bearer JWT for app.wellnuo.com — and that token lives in two
    // different localStorage slots depending on how the user signed in:
    //
    //   - /auto-login (WebView): sets BOTH `accessToken` (modern JWT) and
    //     `auth2.token` (LEGACY well-api token — useless for /api/me/*).
    //   - /login form (browser): sets only `auth2.token`, which IS the
    //     modern JWT in that flow (saveAuthFromResponse writes the same
    //     /api/auth/login response token used by verify-otp).
    //
    // So prefer accessToken (definitely modern when set) and fall back to
    // auth2.token (modern for /login users, legacy + 401 for the legacy
    // native-shell path — acceptable; those users have other auth problems).
    // Decode a JWT payload WITHOUT verifying the signature — only to inspect its
    // claims client-side. Returns the payload object, or null if not a JWT.
    private static jwtPayload(token: string): any | null {
        try {
            const part = token.split('.')[1]
            if (!part) return null
            return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
        } catch {
            return null
        }
    }

    private static walksAuthHeaders(beneficiaryUserId?: number | null): Record<string, string> {
        // Admin view token (admin.wellnuo.com Dashboard tab → auto-login #vt=):
        // read-only, bound to ONE beneficiary, 60 min. It wins over every other
        // token, but only for GET feeds of that same beneficiary (callers pass
        // beneficiaryUserId only on those); the backend refuses it anywhere else.
        if (beneficiaryUserId != null) {
            try {
                const vt = sessionStorage.getItem('viewToken')
                const p = vt ? this.jwtPayload(vt) : null
                if (vt && p && p.scope === 'admin_view' && p.viewAs === beneficiaryUserId
                    && typeof p.exp === 'number' && p.exp * 1000 > Date.now()) {
                    return { 'Authorization': `Bearer ${vt}` }
                }
            } catch { /* storage blocked — fall through */ }
        }
        // /api/me/* needs a MODERN wellnuo-backend JWT (claims include `userId` +
        // `tokenVersion`). Two traps this guards against:
        //  1. The legacy `auth2.token` is `{ email, exp }` ONLY — no `userId`.
        //     Sent as a Bearer, the backend resolves user_id=undefined → "User
        //     not found" 401, AND (since the middleware prefers the Authorization
        //     header over the cookie) it would OVERRIDE the valid HttpOnly cookie.
        //     So we never send a non-modern token as a Bearer.
        //  2. The modern token may live in localStorage.accessToken (auto-login)
        //     OR only as the HttpOnly `accessToken` cookie. When we have no modern
        //     token in localStorage we send NO Authorization header and let the
        //     cookie (via withCredentials) authenticate.
        const now = Date.now()
        const candidates: string[] = []
        const at = localStorage.getItem('accessToken')
        if (at) candidates.push(at)
        try {
            const a = JSON.parse(localStorage.getItem('auth2') || 'null')
            if (a && typeof a.token === 'string') candidates.push(a.token)
        } catch { /* malformed auth2 — ignore */ }

        const modern = candidates
            .map(t => ({ t, p: this.jwtPayload(t) }))
            .filter(c => c.p && c.p.userId != null && (typeof c.p.exp !== 'number' || c.p.exp * 1000 > now))
            .sort((a, b) => ((b.p.exp ?? 0) - (a.p.exp ?? 0)))
        return modern.length ? { 'Authorization': `Bearer ${modern[0].t}` } : {}
    }

    // 404 = backend hasn't rolled out yet OR no walks recorded.
    // 403 = caregiver has legacy access to the deployment but no modern
    //   consent/access record on wellnuo-backend (legitimate product state,
    //   not just a contract bug — render as empty + a soft hint, not red).
    static async getWalks(beneficiaryUserId: number, from?: number, to?: number): Promise<WalkModel[]> {
        const params: Record<string, string> = {}
        if (from !== undefined) params.from = String(from)
        if (to !== undefined) params.to = String(to)
        const rsp = await wellnuoClient.request<{ walks?: WalkModel[] }>({
            url: `/api/me/beneficiaries/${beneficiaryUserId}/walks`,
            method: 'GET',
            headers: this.walksAuthHeaders(beneficiaryUserId),
            params
        })
        // 401 = no/stale modern token for /api/me/* (e.g. token slot mismatch);
        // treat it like 403/404 — render the soft empty state, not a red error.
        if (rsp.status === 404 || rsp.status === 403 || rsp.status === 401) return []
        if (rsp.status !== 200) throw new Error(`Walks unavailable (${rsp.status})`)
        return Array.isArray(rsp.data?.walks) ? rsp.data.walks! : []
    }

    static async getWalkPoints(beneficiaryUserId: number, walkId: string): Promise<WalkPointModel[]> {
        const rsp = await wellnuoClient.request<{ points?: WalkPointModel[] }>({
            url: `/api/me/beneficiaries/${beneficiaryUserId}/walks/${walkId}/points`,
            method: 'GET',
            headers: this.walksAuthHeaders(beneficiaryUserId)
        })
        if (rsp.status === 404 || rsp.status === 403 || rsp.status === 401) return []
        if (rsp.status !== 200) throw new Error(`Walk points unavailable (${rsp.status})`)
        return Array.isArray(rsp.data?.points) ? rsp.data.points! : []
    }

    // Latest background-protection posture from the beneficiary's phone, read
    // off the beneficiary detail. Best-effort: any non-200 (404 before the
    // backend rolled out, 403 no modern access) → null → banner stays hidden.
    static async getDeviceHealth(beneficiaryUserId: number): Promise<DeviceHealth | null> {
        try {
            const rsp = await wellnuoClient.request<{ deviceHealth?: DeviceHealth | null }>({
                url: `/api/me/beneficiaries/${beneficiaryUserId}`,
                method: 'GET',
                headers: this.walksAuthHeaders(beneficiaryUserId)
            })
            if (rsp.status !== 200) return null
            return rsp.data?.deviceHealth ?? null
        } catch {
            return null
        }
    }

    // Locate-Now: fires a silent push to the beneficiary's phone asking for an
    // on-demand GPS fix. POST returns a requestId; the caller polls the GET
    // every ~2s until status leaves "pending". Same Bearer-JWT path as walks.
    // Locate-Now 403 is a legitimate product state (no modern consent record
    // for this beneficiary on wellnuo-backend) — sentinel "access_denied"
    // message so LocateNow renders a soft "not enabled" panel instead of a
    // red "Couldn't start locate request" error.
    static async requestLocate(beneficiaryUserId: number): Promise<LocateRequestResponse> {
        const rsp = await wellnuoClient.request<LocateRequestResponse & { error?: string }>({
            url: `/api/me/beneficiaries/${beneficiaryUserId}/locate`,
            method: 'POST',
            headers: this.walksAuthHeaders()
        })
        if (rsp.status === 403) throw new Error('access_denied')
        if (rsp.status !== 200 || !rsp.data?.requestId) {
            throw new Error(rsp.data?.error || `Couldn't start locate request (${rsp.status})`)
        }
        return { requestId: rsp.data.requestId }
    }

    static async getLocateStatus(beneficiaryUserId: number, requestId: string): Promise<LocateStatusResponse> {
        const rsp = await wellnuoClient.request<LocateStatusResponse & { error?: string }>({
            url: `/api/me/beneficiaries/${beneficiaryUserId}/locate/${requestId}`,
            method: 'GET',
            headers: this.walksAuthHeaders(beneficiaryUserId)
        })
        if (rsp.status === 403) throw new Error('access_denied')
        if (rsp.status !== 200 || !rsp.data?.status) {
            throw new Error(rsp.data?.error || `Locate status unavailable (${rsp.status})`)
        }
        return rsp.data
    }

    // Blood-pressure readings for a whole deployment (account), via the legacy
    // well-api `get_bp_data` function extended to accept `deployment_id` (it
    // resolves the deployment's BM67 BP monitors server-side — see
    // HANDOFF_TO_SERVER_blood_pressure_2026-06-07.md).
    //
    // SOFT-FAIL by design: any failure — endpoint not deployed yet (legacy 500
    // "unknown function"), bad status, or a missing readings array — returns
    // [] rather than throwing. The Blood Pressure panel only renders when there
    // are readings, so an empty result simply hides it ("otherwise do not add
    // it") and leaves the rest of the dashboard untouched. A genuine session
    // expiry is already caught by the getUserById call that loads the page, so
    // we deliberately don't call throwSessionExpired() here.
    static async getBloodPressure(deploymentId: number, days: number = 30): Promise<BloodPressureReading[]> {
        const auth: AuthModel = AuthService.getAuth()
        try {
            const rsp = await client.request<BloodPressureResponse>({
                url: '/function/well-api/api',
                method: 'POST',
                data: {
                    "function": "get_bp_data",
                    "user_name": auth.username,
                    "token": auth.token,
                    "deployment_id": deploymentId,
                    "days": days,
                    "nonce": uuidv4()
                }
            })
            if (rsp.status !== 200 || rsp.data?.status !== "200 OK" || !Array.isArray(rsp.data?.readings)) {
                return []
            }
            return rsp.data.readings!
        } catch {
            return []
        }
    }

    // Watch Health Services feeds (heart rate / daily totals / activity state),
    // keyed by BENEFICIARY user id (person_user_id), NOT deployment id — watch
    // data is per-person. These live on wellnuo-backend (/api/me/*), so they use
    // the modern-JWT walksAuthHeaders() path, same as getWalks/getDeviceHealth.
    //
    // SOFT-FAIL by design: 401/403/404 are legitimate states (backend not rolled
    // out yet, no modern access edge, token-slot mismatch) — return empty so the
    // WatchHealthReport panel self-hides, never a red error. A real session
    // expiry is already caught by the getUserById that loads the page, so we
    // deliberately don't call throwSessionExpired() here.
    static async getWatchHeartRate(beneficiaryUserId: number, days = 30): Promise<{ samples: WatchHrSample[]; bucketed: boolean }> {
        try {
            const rsp = await wellnuoClient.request<{ samples?: WatchHrSample[]; bucketed?: boolean }>({
                url: `/api/me/beneficiaries/${beneficiaryUserId}/watch/health/heart-rate`,
                method: 'GET', headers: this.walksAuthHeaders(beneficiaryUserId), params: { days: String(days) },
            })
            if (rsp.status !== 200) return { samples: [], bucketed: false }
            return { samples: Array.isArray(rsp.data?.samples) ? rsp.data.samples! : [], bucketed: rsp.data?.bucketed === true }
        } catch { return { samples: [], bucketed: false } }
    }

    static async getWatchDaily(beneficiaryUserId: number, days = 30): Promise<WatchDay[]> {
        try {
            const rsp = await wellnuoClient.request<{ days?: WatchDay[] }>({
                url: `/api/me/beneficiaries/${beneficiaryUserId}/watch/health/daily`,
                method: 'GET', headers: this.walksAuthHeaders(beneficiaryUserId), params: { days: String(days) },
            })
            return rsp.status === 200 && Array.isArray(rsp.data?.days) ? rsp.data.days! : []
        } catch { return [] }
    }

    static async getWatchActivityState(beneficiaryUserId: number, days = 7): Promise<WatchActivityState[]> {
        try {
            const rsp = await wellnuoClient.request<{ states?: WatchActivityState[] }>({
                url: `/api/me/beneficiaries/${beneficiaryUserId}/watch/health/activity-state`,
                method: 'GET', headers: this.walksAuthHeaders(beneficiaryUserId), params: { days: String(days) },
            })
            return rsp.status === 200 && Array.isArray(rsp.data?.states) ? rsp.data.states! : []
        } catch { return [] }
    }

    // Sleep sound (snoring) nightly summaries + one night's timeline, keyed by
    // BENEFICIARY user id (person_user_id). Same soft-fail contract as the
    // watch feeds: 401/403/404 (backend not rolled out, no modern access edge,
    // no data) → empty, so SleepSoundReport self-hides. The backend clamps
    // `days` to 366, so the "all history" fallback can pass anything larger.
    static async getSleepSoundNights(beneficiaryUserId: number, days = 30): Promise<SleepSoundNight[]> {
        try {
            const rsp = await wellnuoClient.request<{ nights?: SleepSoundNight[] }>({
                url: `/api/me/beneficiaries/${beneficiaryUserId}/sleep-sound/nights`,
                method: 'GET', headers: this.walksAuthHeaders(beneficiaryUserId), params: { days: String(days) },
            })
            return rsp.status === 200 && Array.isArray(rsp.data?.nights) ? rsp.data.nights! : []
        } catch { return [] }
    }

    // null = no data for that night (404) or any failure.
    static async getSleepSoundNight(beneficiaryUserId: number, date: string, bucket = 5): Promise<SleepSoundNightDetail | null> {
        try {
            const rsp = await wellnuoClient.request<SleepSoundNightDetail>({
                url: `/api/me/beneficiaries/${beneficiaryUserId}/sleep-sound/night/${date}`,
                method: 'GET', headers: this.walksAuthHeaders(beneficiaryUserId), params: { bucket: String(bucket) },
            })
            return rsp.status === 200 && Array.isArray(rsp.data?.buckets) ? rsp.data : null
        } catch { return null }
    }

    // Radar time-in-bed for one night, by DEPLOYMENT id. Used for the Sleep
    // Report's past-date tile because well-api dashboard_single ignores `date`
    // (backend commit bf7c1ef) and would show today's number. `date` is the
    // morning the night ends. null = no night recorded or any failure.
    static async getRadarSleepNight(deploymentId: number, date: string): Promise<RadarSleepNight | null> {
        try {
            const rsp = await wellnuoClient.request<{ nights?: RadarSleepNight[] }>({
                url: `/api/legacy/sleep-summary/${deploymentId}`,
                method: 'GET', headers: this.walksAuthHeaders(), params: { from: date, to: date },
            })
            if (rsp.status !== 200 || !Array.isArray(rsp.data?.nights)) return null
            return rsp.data.nights!.find(n => n.date === date) ?? null
        } catch { return null }
    }

    private static throwSessionExpired() {
        AuthService.clearAuth()
        Swal.fire({
            icon: "error",
            title: "Session Expired",
            text: "Your session has expired! Please log in again to continue.",
            customClass: customSwalStyling
        })
        throw new Error('UNAUTHORIZED')
    }
}